import * as React from 'react'

import { Button } from '../../../main/components/button'
import { SpecificChartFunctionality, TIMEFRAME } from '../../../main/constants'
import { Instrument } from '../../../main/types'
import { encodeQueryString } from '../../queryString'
import { FuturesForexCryptoTimeframeBar } from '../../shared/components/FuturesForexCryptoTimeframeBar'
import { PageType } from '../types'
import { groups } from '../utils'

interface MenuProps {
  company?: string
  activePage: PageType
  activeGroup?: string
  activeRange?: string
  showGroups?: boolean
  type?: string
  onGroupMouseEnter?: (group: string) => void
  onGroupMouseLeave?: () => void
  timeframes: {
    active: string
    link?: (timeframe: string) => string
    onClick: (timeframe: string, e: React.MouseEvent<HTMLElement>) => void
    timeframes: Array<{ key: string; title?: string; value: React.ReactNode }>
  }
  groupTimeframe?: string
  isDetailOpen?: boolean
  onTimeframeChange?: (timeframe: TIMEFRAME) => void
  onDateRangeChange?: (dateRange: string | undefined) => void
}

export function MenuNew({
  company = '',
  activePage,
  activeGroup = 'all',
  activeRange,
  showGroups = true,
  type,
  timeframes,
  groupTimeframe = 'd',
  isDetailOpen,
  onTimeframeChange,
  onDateRangeChange,
  ...props
}: MenuProps) {
  const groupName = company ?? activeGroup.charAt(0).toUpperCase() + activeGroup.slice(1)

  return (
    <div className="flex h-12 items-center justify-between">
      <div className="flex w-64 space-x-px font-sans xl:w-96">
        <Button as="a" href="/futures.ashx" active={activePage === PageType.Quotes} theme="chipTransparent">
          Quotes
        </Button>
        <Button
          as="a"
          href="/futures_performance.ashx"
          theme="chipTransparent"
          active={activePage === PageType.Performance}
        >
          Performance
        </Button>
        <Button as="a" href="/futures_charts.ashx" theme="chipTransparent" active={activePage === PageType.Charts}>
          Charts
        </Button>
      </div>
      {showGroups && (
        <div className="flex space-x-px font-sans">
          <Button
            as="a"
            theme="chipTransparent"
            href={`/futures_charts.ashx?${encodeQueryString({
              p: groupTimeframe,
              ty: type === '1' ? undefined : type,
              r: activeRange,
            })}`}
            active={'all' === activeGroup}
          >
            All
          </Button>
          {groups.map((group) => (
            <Button
              key={group}
              as="a"
              theme="chipTransparent"
              href={`/futures_charts.ashx?${encodeQueryString({
                t: group.toUpperCase(),
                p: groupTimeframe,
                ty: type === '1' ? undefined : type,
                r: activeRange,
              })}`}
              active={group.toLowerCase() === activeGroup}
              onMouseEnter={() => props.onGroupMouseEnter?.(group.toUpperCase())}
              onMouseLeave={props.onGroupMouseLeave}
            >
              {group}
            </Button>
          ))}
        </div>
      )}
      <div className="flex w-64 justify-end space-x-px font-sans xl:w-96">
        {activePage === PageType.Charts ? (
          <FuturesForexCryptoTimeframeBar
            isDetail={isDetailOpen}
            title="Futures"
            group={activeGroup === 'all' ? undefined : groupName}
            onTimeframeChange={onTimeframeChange}
            onDateRangeChange={onDateRangeChange}
            specificChartFunctionality={SpecificChartFunctionality.futuresPage}
            instrument={Instrument.Futures}
          />
        ) : (
          timeframes.timeframes.map((timeframe) => (
            <Button
              key={timeframe.key}
              as={timeframes.link ? 'a' : 'button'}
              {...{ href: timeframes.link?.(timeframe.key) }}
              theme="chipTransparent"
              padding="small"
              active={timeframes.active === timeframe.key}
              onClick={(ev: React.MouseEvent<HTMLButtonElement>) => timeframes.onClick?.(timeframe.key, ev)}
              title={timeframe.title}
            >
              {timeframe.value}
            </Button>
          ))
        )}
      </div>
    </div>
  )
}
